import type { AiAssistExample } from '@/design/AiAssistComposer';
import type { ClassificationOutputType, ClassificationStatus } from './ClassificationService';

/**
 * The category a classification belongs to. Each value maps 1:1 to one of the
 * analytics surfaces it is authored from — see `SURFACE_CLASSIFICATION_CATEGORY`.
 */
export const CATEGORY_OPTIONS = ['Adoption', 'Usage', 'Governance', 'FinOps'] as const;

/**
 * Maps an analytics surface id to the classification category it owns. The
 * classification builder is reached from a card on each of these surfaces with
 * the category pre-filled and locked. `ai-explorer` is intentionally absent — it
 * has no classification card.
 */
export const SURFACE_CLASSIFICATION_CATEGORY: Record<string, string> = {
    behavior: 'Usage',
    governance: 'Governance',
    'token-usage': 'FinOps',
};

/**
 * Categories whose owning surface isn't the 1:1 inverse of the map above —
 * Adoption is now a section of the Behavior surface, so its classifications
 * belong to `/behavior`.
 */
const CATEGORY_SURFACE_OVERRIDE: Record<string, string> = {
    Adoption: 'behavior',
};

/**
 * Resolves a route param (the lower-cased category, e.g. `finops`) back to its
 * canonical category label. Returns `undefined` for an unknown param.
 */
export function categoryFromParam(param: string | undefined): string | undefined {
    if (!param) return undefined;
    return CATEGORY_OPTIONS.find((c) => c.toLowerCase() === param.toLowerCase());
}

/** The surface id that owns a category (e.g. "FinOps" -> "token-usage"). Inverse of `SURFACE_CLASSIFICATION_CATEGORY`. */
export function surfaceForCategory(category: string): string | undefined {
    return CATEGORY_SURFACE_OVERRIDE[category] ?? Object.keys(SURFACE_CLASSIFICATION_CATEGORY).find((id) => SURFACE_CLASSIFICATION_CATEGORY[id] === category);
}

/** The noun used for a category's classifications in the UI (e.g. "Policy" for Governance). */
export interface CategoryNoun {
    singular: string;
    plural: string;
}

const CATEGORY_NOUNS: Record<string, CategoryNoun> = {
    Governance: { singular: 'Policy', plural: 'Policies' },
};

const DEFAULT_NOUN: CategoryNoun = { singular: 'Classification', plural: 'Classifications' };

/** The display noun for a category, falling back to "Classification(s)". */
export function categoryNoun(category: string): CategoryNoun {
    return CATEGORY_NOUNS[category] ?? DEFAULT_NOUN;
}

const DEFAULT_ASSIST_EXAMPLES: AiAssistExample[] = [
    { label: 'Confidential data', value: 'Detect people pasting confidential data into prompts and flag the severity 1–5.' },
    { label: 'Abandoned chats', value: 'Measure reluctance — users who open a chat but abandon it before getting a useful answer.' },
    { label: 'Prompt quality', value: 'Rate prompt quality from 1–5 with a short explanation of what made it strong or weak.' },
];

const CATEGORY_ASSIST_EXAMPLES: Record<string, AiAssistExample[]> = {
    Adoption: [
        { label: 'First-week activation', value: 'Score how activated each new user is from 1–5 based on whether they returned and did real work in their first week.' },
        { label: 'Daily habit', value: 'Flag users who have made AI a daily habit versus occasional dabblers, based on how consistently they return.' },
        { label: 'Feature breadth', value: 'Detect users who only ever use one feature and never explore the rest of what the product offers.' },
    ],
    Usage: [
        { label: 'Task mix', value: 'Classify each session by the kind of work it was — coding, writing, analysis, or research.' },
        { label: 'Model fit', value: 'Flag sessions where a heavyweight model was used for a trivial task and rate the mismatch 1–5.' },
        { label: 'Session depth', value: 'Measure how deep each conversation went — a one-shot question versus an extended back-and-forth.' },
    ],
    Governance: [
        { label: 'Confidential data', value: 'Detect people pasting confidential or customer data into prompts and flag the severity 1–5.' },
        { label: 'Work vs personal', value: 'Classify whether each session was work-related or personal use.' },
        { label: 'Off-policy tools', value: 'Flag sessions that used an unapproved model or external tool against company policy.' },
    ],
    FinOps: [
        { label: 'Premium-model waste', value: 'Flag developers using premium models for simple formatting or grammar fixes, and score how wasteful each session was 1–10.' },
        { label: 'Cost per session', value: 'Estimate the dollar cost of each session and surface the most expensive ones.' },
        { label: 'Abandoned spend', value: 'Detect expensive sessions that were abandoned before producing any useful output.' },
    ],
};

/** Category-specific AI-assist example prompts, falling back to a generic set. */
export function categoryAssistExamples(category: string): AiAssistExample[] {
    return CATEGORY_ASSIST_EXAMPLES[category] ?? DEFAULT_ASSIST_EXAMPLES;
}

export interface StatusMeta {
    value: ClassificationStatus;
    label: string;
    color: string;
}

export const STATUS_OPTIONS: StatusMeta[] = [
    { value: 'draft', label: 'Draft', color: 'gray' },
    { value: 'active', label: 'Active', color: 'teal' },
    { value: 'inactive', label: 'Inactive', color: 'orange' },
];

export function statusMeta(status: ClassificationStatus): StatusMeta {
    return STATUS_OPTIONS.find((s) => s.value === status) ?? STATUS_OPTIONS[0]!;
}

export const CURRENCY_OPTIONS = ['USD', 'EUR', 'GBP', 'CAD', 'AUD'] as const;

export interface OutputTypeMeta {
    value: ClassificationOutputType;
    label: string;
    badge: string;
    description: string;
}

export const OUTPUT_TYPES: OutputTypeMeta[] = [
    {
        value: 'scale',
        label: 'Scale',
        badge: 'SCALE',
        description: 'Returns a number on a fixed range. The model rates each session from a low to a high bound — useful for severity or quality scoring.',
    },
    {
        value: 'currency',
        label: 'Currency',
        badge: 'CURRENCY',
        description: 'Returns a monetary amount in the chosen currency — useful for estimating cost or waste per session.',
    },
    {
        value: 'percent',
        label: 'Percent',
        badge: 'PERCENT',
        description: 'Returns a percentage from 0 to 100 — useful for proportions like confidence or coverage.',
    },
    {
        value: 'text',
        label: 'Text',
        badge: 'TEXT',
        description: 'Returns a short free-text answer or explanation for each session.',
    },
    {
        value: 'category',
        label: 'Category',
        badge: 'CATEGORY',
        description: 'Returns one of a fixed set of labels you define — useful for bucketing sessions.',
    },
];

/** A sensible default config when the user switches output type. */
export function defaultOutputConfig(type: ClassificationOutputType): Record<string, unknown> {
    switch (type) {
        case 'scale':
            return { min: 1, max: 10 };
        case 'currency':
            return { currencyCode: 'USD' };
        case 'category':
            return { options: [] };
        case 'percent':
        case 'text':
        default:
            return {};
    }
}

/** A short human-readable summary of a scale config, e.g. "1–10". */
export function describeOutputConfig(type: ClassificationOutputType, config: Record<string, unknown>): string {
    if (type === 'scale') {
        const min = typeof config.min === 'number' ? config.min : '?';
        const max = typeof config.max === 'number' ? config.max : '?';
        return `${min}–${max}`;
    }
    if (type === 'currency') {
        return typeof config.currencyCode === 'string' ? config.currencyCode : 'USD';
    }
    if (type === 'category') {
        const options = Array.isArray(config.options) ? config.options : [];
        return `${options.length} option${options.length === 1 ? '' : 's'}`;
    }
    return '';
}
